import React, { useEffect, useRef, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import { useSnackbar } from "notistack";
import { _member_list_with_search } from "src/DAL/Chat/Chat";
import { s3baseUrl } from "src/config/config";
import { gr } from "src/design-system/tokens";
import GrLoader from "src/design-system/components/GrLoader";
import GrSearch from "src/design-system/components/GrSearch";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrIcon from "src/design-system/components/GrIcon";
import icons from "src/design-system/icons";
import GrBadge from "src/design-system/components/GrBadge";
import GrChatListRow from "src/design-system/components/GrChatListRow";

const ROLE_LABELS = { coach: "coach", admin: "admin", friend: "friend" };
// Role chips: coach = soft teal, admin = teal outline, friend = neutral grey (same words as before).
const ROLE_TONES = { coach: "brand", admin: "outline", friend: "neutral" };

// Display only: "Jane Doe (coach)" -> name "Jane Doe" + a "Coach" badge.
const splitRole = (fullName = "") => {
  const match = /^(.*?)\s*\(([^()]+)\)\s*$/.exec(String(fullName));
  if (!match) return { name: fullName, role: "" };
  const key = match[2].trim().toLowerCase();
  return { name: match[1], role: ROLE_LABELS[key] || match[2].trim() };
};

const titleSx = {
  m: 0,
  mt: gr.space[5],
  mb: gr.space[2],
  fontFamily: gr.font.family,
  // "Users List" as written (no admin-style uppercase), section label size.
  fontSize: gr.font.size.sm,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.ink2,
};

const listSx = {
  mx: `calc(-1 * ${gr.space[4]})`,
  borderTop: `1px solid ${gr.color.line2}`,
  borderBottom: `1px solid ${gr.color.line2}`,
};

const loadMoreSx = {
  textAlign: "center",
  py: gr.space[3],
  fontSize: gr.font.size.sm,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.brandDark,
  cursor: "pointer",
};

export default function NewChatUsers({ handleAddChat, onCloseDrawer }) {
  const observer = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const [chatUsersList, setChatUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [moreLoad, setMoreLoad] = useState(false);
  const [loadMorePath, setLoadMorePath] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const load_more_path = "api/chat/users?page=0&limit=20";
  const [state, setState] = useState({
    search_text: "",
  });

  const getChatUsersList = async (path, search_text, loading) => {
    if (!loading) {
      path = load_more_path;
    }
    setSearchLoading(true);
    const result = await _member_list_with_search(path, search_text);
    if (result.code === 200) {
      setLoadMorePath(result?.load_more_url);
      setMoreLoad(result?.pagination?.hasNextPage);
      let new_coaches_list = [];
      if (result?.users?.length > 0) {
        new_coaches_list = result.users
          .filter(
            (user) =>
              user.details &&
              (Array.isArray(user.details) ? user.details.length > 0 : true)
          )
          .flatMap((user) => {
            if (user.rolse === "friends" && Array.isArray(user.details)) {
              return user.details.map((detail) => ({
                ...detail,
                full_name: `${detail.first_name} ${detail.last_name} (friend)`,
                profile_image: detail?.profile_image || "",
                email: "",
              }));
            } else if (user.role === "coach" && Array.isArray(user.details)) {
              return user.details.map((detail) => ({
                ...detail,
                full_name: `${detail?.name} (coach)`,
                profile_image: detail?.image?.thumbnail_1 || "",
                email: "",
              }));
            } else {
              const details = user.details;
              if (Object.keys(details).length > 0) {
                return {
                  ...details,
                  full_name: `${details.name} (${user.role})`,
                  profile_image: details?.image?.thumbnail_1,
                };
              }
            }
          });
      }

      if (loading) {
        setChatUsersList((prevList) => [...prevList, ...new_coaches_list]);
      } else {
        setChatUsersList(new_coaches_list);
      }
      setLoading(false);
      setSearchLoading(false);
    } else {
      setLoading(false);
      setSearchLoading(false);
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const loadMoreData = () => {
    setIsLoadingMore(true);
    getChatUsersList(loadMorePath, state.search_text, true);
  };

  const lastBookElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && moreLoad) {
          loadMoreData();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, moreLoad, loadMorePath, observer]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setState((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    if (!loading) {
      const timeoutId = setTimeout(() => {
        getChatUsersList(load_more_path, state.search_text);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [state.search_text]);

  useEffect(() => {
    getChatUsersList(load_more_path, state.search_text);
  }, []);

  if (loading) {
    return (
      <GrLoader />
    );
  }

  return (
    <Box sx={{ fontFamily: gr.font.family, p: gr.space[4] }}>
      {/* Search + list title stay on top while the list scrolls under them. */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          mx: `calc(-1 * ${gr.space[4]})`,
          mt: `calc(-1 * ${gr.space[4]})`,
          px: gr.space[4],
          pt: gr.space[4],
          background: gr.color.surface,
        }}
      >
        <GrSearch
          placeholder="Search"
          label="Search"
          name="search_text"
          value={state.search_text}
          onChange={handleChange}
        />
        <Box component="h3" sx={titleSx}>
          Users List
        </Box>
      </Box>
      {searchLoading ? (
        <GrLoader size="sm" />
      ) : (
        <Box sx={listSx}>
          {state.search_text &&
          chatUsersList.filter((user) => !!user).length === 0 ? (
            <GrEmptyState
              compact
              icon={<GrIcon icon={icons.people} />}
              title="Users Not Found"
            />
          ) : null}
          {chatUsersList.map((user, index) => {
            if (!!user) {
              const role = splitRole(user?.full_name);
              return (
                <GrChatListRow
                  key={user?._id ?? index}
                  onClick={() => {
                    handleAddChat(user._id, onCloseDrawer);
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set("chat_id", user._id);
                    window.history.replaceState(null, "", newUrl.toString());
                  }}
                  src={s3baseUrl + user?.profile_image}
                  name={role.name}
                  online={!!user?.is_online}
                  tag={
                    role.role ? (
                      <GrBadge tone={ROLE_TONES[role.role] || "neutral"}>
                        {role.role}
                      </GrBadge>
                    ) : null
                  }
                  preview={user?.email}
                />
              );
            }
            return null;
          })}
        </Box>
      )}

      {moreLoad ? (
        <Box sx={loadMoreSx}>
          <span
            ref={lastBookElementRef}
            onClick={loadMoreData}
            id="load-more-feed"
          >
            {isLoadingMore ? (
              <GrLoader size="sm" inline />
            ) : (
              "Load More"
            )}
          </span>
        </Box>
      ) : (
        ""
      )}
    </Box>
  );
}
